/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";

import { CustomError } from "@Internal/Models";
import { Autobind } from "@Internal/Utils";

import {
    ApiKey, ApiKeyPage, ApiKeyRequest, ApiKeyStatus, GeneratedApiKey
} from "App/Models/ApiKey";

import { ProcessException } from "./Base";

type Result<T> = Promise<[ T | undefined, CustomError | undefined ]>;

/* Les cles API des clients. Le back relaie `console/api-key/*` sans le
   serialiser ( exemption `Internal/ApiKeys` de `Web/Hooks.ts` ) : on lit la
   reponse telle quelle. Une erreur de l'API revient en objet `{ Id, Message }`,
   `Id` = son code HTTP ( 404, 409… ). */
export default class ApiKeysService {
    private service: Feathers.Service<any>;

    constructor( service: Feathers.Service<any> ) {
        this.service = service;
    }

    @Autobind
    async List( page: number, limit: number ): Result<ApiKeyPage> {
        try {
            const result = await this.service.find( { query: { page, limit } } );

            if( result === undefined || result._embedded === undefined )
                return [ undefined, this.error( result, "ApiKeys.List" ) ];

            const items: any[] = Array.isArray( result._embedded.items )
                ? result._embedded.items
                : [];

            return [ {
                Keys: items.map( deserialize ),
                Total: Number( result.total ) || items.length
            }, undefined ];
        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    /* Le jeton en clair. `undefined` sans erreur quand l'API ne le connait pas :
       clef revoquee, expiree, ou anterieure au 06/10/2026. */
    @Autobind
    async Read( id: number ): Result<string | undefined> {
        try {
            const result = await this.service.get( id );

            if( result === undefined || result.id === undefined )
                return [ undefined, this.error( result, "ApiKeys.Read" ) ];

            return [ ( typeof result.token === "string" ) ? result.token : undefined, undefined ];
        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async Generate( request: ApiKeyRequest ): Result<GeneratedApiKey> {
        try {
            const result = await this.service.create( {
                user_id: request.UserId,
                name: request.Name,
                expires_at: request.ExpiresAt
            } );

            if( result === undefined || result.data === undefined )
                return [ undefined, this.error( result, "ApiKeys.Generate" ) ];

            return [ {
                Key: deserialize( result.data ),
                Token: String( result.data.token || "" )
            }, undefined ];
        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    /* Definitif. 409 si la clef l'est deja. */
    @Autobind
    async Revoke( id: number ): Promise<CustomError | undefined> {
        try {
            const result = await this.service.remove( id );

            if( result === undefined || result.status !== "success" )
                return this.error( result, "ApiKeys.Revoke" );

            return undefined;
        } catch( error ) {
            return ProcessException( error );
        }
    }

    private error( result: any, fallbackId: string ): CustomError {
        const id = ( result !== undefined && result.Id !== undefined ) ? result.Id : fallbackId;
        const message = ( result !== undefined && result.Message !== undefined )
            ? result.Message
            : "Unexpected API key response";

        return new CustomError( id, message );
    }
}

function text( value: unknown ): string {
    return ( value === undefined || value === null ) ? "" : String( value );
}

function deserialize( raw: any ): ApiKey {
    /* L'API calcule `is_active` et `is_expired` : on la croit plutot que de
       comparer des dates a l'horloge du poste. */
    let status: ApiKeyStatus = "active";

    if( text( raw.revoked_at ) !== "" )
        status = "revoked";
    else if( raw.is_expired === true || raw.is_active === false )
        status = "expired";

    return {
        Id: Number( raw.id ),
        UserId: Number( raw.user_id ),
        UserEmail: text( raw.user_email ),
        Name: text( raw.name ),
        Prefix: text( raw.prefix ),
        Status: status,
        CreatedAt: text( raw.created_at ),
        LastUsedAt: text( raw.last_used_at ),
        RevokedAt: text( raw.revoked_at ),
        ExpiresAt: text( raw.expires_at )
    };
}
